import { MetaManager } from "@/components/seo/MetaManager";
import { MainNavigation } from "@/components/layout/MainNavigation";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { EnhancedFAQs } from "@/components/seo/EnhancedFAQs";
import { AuthorBio } from "@/components/content/AuthorBio";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { RelatedResources } from "@/components/seo/RelatedResources";
import { BreadcrumbNavigation } from "@/components/seo/BreadcrumbNavigation";

const SOUTH_STATES = [
  { name: 'Alabama', abbr: 'AL', url: 'alabama' },
  { name: 'Arkansas', abbr: 'AR', url: 'arkansas' },
  { name: 'Delaware', abbr: 'DE', url: 'delaware' },
  { name: 'Florida', abbr: 'FL', url: 'florida' },
  { name: 'Georgia', abbr: 'GA', url: 'georgia' },
  { name: 'Kentucky', abbr: 'KY', url: 'kentucky' },
  { name: 'Louisiana', abbr: 'LA', url: 'louisiana' },
  { name: 'Maryland', abbr: 'MD', url: 'maryland' },
  { name: 'Mississippi', abbr: 'MS', url: 'mississippi' },
  { name: 'North Carolina', abbr: 'NC', url: 'north-carolina' },
  { name: 'Oklahoma', abbr: 'OK', url: 'oklahoma' },
  { name: 'South Carolina', abbr: 'SC', url: 'south-carolina' },
  { name: 'Tennessee', abbr: 'TN', url: 'tennessee' },
  { name: 'Texas', abbr: 'TX', url: 'texas' },
  { name: 'Virginia', abbr: 'VA', url: 'virginia' },
  { name: 'West Virginia', abbr: 'WV', url: 'west-virginia' }
];

const SOUTH_FAQS = [
  {
    question: "What are the advantages of using PEOs in Right-to-Work states?",
    answer: "Many Southern states are Right-to-Work, which creates opportunities for PEOs to provide flexible employment solutions. PEOs can help businesses navigate the nuances of Right-to-Work laws while still maintaining competitive benefits and workplace policies that attract top talent."
  },
  {
    question: "How do Southern PEOs handle workers' compensation in high-risk industries?",
    answer: "Southern PEOs often specialize in industries like construction, manufacturing, and energy that are prevalent in the region. They have experience managing workers' compensation in high-risk environments and relationships with carriers who understand Southern industry exposures."
  },
  {
    question: "Do Southern PEOs understand regional business culture?",
    answer: "Yes, established Southern PEOs understand the importance of relationship-building, community involvement, and the more personal approach to business that characterizes much of the South. They tailor their service delivery to match regional expectations and communication styles."
  },
  {
    question: "How do PEOs help with the South's rapid business growth?",
    answer: "Southern PEOs are experienced in scaling operations quickly to support the region's booming economy. They provide infrastructure for rapid hiring, multi-location management, and the operational flexibility needed to capitalize on growth opportunities in expanding Southern markets."
  }
];

const RELATED_RESOURCES = [
  {
    title: "Texas PEO Services",
    description: "Complete guide to PEO services in Texas",
    url: "/states/texas",
    type: "state-guide" as const
  },
  {
    title: "Florida PEO Providers",
    description: "Find the best PEO companies in Florida",
    url: "/states/florida",
    type: "state-guide" as const
  },
  {
    title: "Construction Industry PEOs",
    description: "Specialized PEO services for construction companies",
    url: "/industries/construction",
    type: "industry-guide" as const
  },
  {
    title: "Manufacturing PEO Services",
    description: "PEO solutions for manufacturing companies",
    url: "/industries/manufacturing",
    type: "industry-guide" as const
  }
];

export const SouthRegion = () => {
  return (
    <div className="min-h-screen bg-background">
      <MetaManager
        title="South PEO Services: Best Professional Employer Organizations in the South"
        description="Find top PEO companies serving the Southern region. Expert comparison of Professional Employer Organizations in TX, FL, GA, NC, VA, and all Southern states. Get matched with the perfect PEO for your business."
        keywords="South PEO, Texas PEO, Florida PEO, Georgia PEO, North Carolina PEO, Virginia PEO, Southern Professional Employer Organization"
        canonical="https://thepeoapp.com/regions/south"
      />
      
      <LocalBusinessSchema
        businessName="The PEO App - South Region"
        serviceType="Professional Employer Organization Services"
        areaServed="Southern United States"
        description="Professional Employer Organization matching services for businesses in the Southern region"
        url="https://thepeoapp.com/regions/south"
      />
      
      <MainNavigation />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation 
            items={[
              { label: "Home", href: "/" },
              { label: "States", href: "/states" },
              { label: "South Region", href: "/regions/south" }
            ]}
          />
          
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              South PEO Services: Professional Employer Organizations
            </h1>
            
            <div className="prose prose-lg max-w-none mb-12 text-muted-foreground">
              <p className="text-xl leading-relaxed">
                The South is America's fastest-growing business region, with booming economies from Texas to Florida. Our vetted Professional Employer Organizations understand Southern business culture and provide the HR infrastructure needed to compete in dynamic markets.
              </p>
            </div>

            <div className="bg-card p-8 rounded-xl shadow-lg mb-12">
              <h2 className="text-2xl font-bold mb-6">Why Choose a Southern PEO?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Regional Advantages</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Understanding of Right-to-Work environments</li>
                    <li>• Experience with rapid business growth</li>
                    <li>• Knowledge of regional industry clusters</li>
                    <li>• Familiarity with Southern business culture</li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Industry Expertise</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Energy and oil & gas sector experience</li>
                    <li>• Manufacturing and logistics knowledge</li>
                    <li>• Tourism and hospitality expertise</li>
                    <li>• Agriculture and food processing</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 p-8 rounded-xl mb-12">
              <h2 className="text-2xl font-bold mb-6">Southern States We Serve</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {SOUTH_STATES.map((state) => (
                  <Link
                    key={state.abbr}
                    to={`/states/${state.url}`}
                    className="bg-background p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center space-x-2 border"
                  >
                    <span className="text-primary font-semibold">{state.abbr}</span>
                    <span className="text-foreground text-sm">{state.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="text-center mb-12 bg-gradient-to-r from-primary/10 to-secondary/10 p-8 rounded-xl">
              <h2 className="text-2xl font-bold mb-4">Ready to Find Your Southern PEO?</h2>
              <p className="text-muted-foreground mb-6">
                Get matched with PEO providers who understand the Southern business advantage
              </p>
              <Link to="/signup">
                <Button size="lg" className="text-lg px-8 py-6">
                  Get Free PEO Quotes
                </Button>
              </Link>
            </div>

            <RelatedResources 
              title="Related Southern Resources"
              resources={RELATED_RESOURCES}
            />

            <div className="mt-16">
              <EnhancedFAQs 
                title="Southern PEO FAQ"
                faqs={SOUTH_FAQS}
                showSchema={true}
              />
            </div>

            <div className="mt-16">
              <AuthorBio showFullBio={false} />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};